import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as RelocationOrderActions from './relocation-order.action';
import { RelocationOrderService } from './relocation-order.service';
import { OrderService } from '../order/order.service';
import { BannerService } from '../../shared/services/banner.service';
import * as RelocationOrderModels from '../../models/relocation-order/relocation-order';
import * as OrderStatusModels from '../../models/order/orderStatus';
import {RelocationOrderSearchModel } from '../../models/relocation-order/relocation-orderSearch';
import { HelperService } from '../../../../src/app/services/helper/helper.service';
import * as Models from '../../models/commonModels';
import { saveAs } from 'file-saver';
import { StorageService } from '../../shared/services/storage.service';

export interface RelocationOrderStateModel {
    OrderStatus: OrderStatusModels.OrderStatusListModel;
    OrderList: RelocationOrderModels.RelocationOrderListModel;
    Order: Models.Response<RelocationOrderModels.RelocationOrderModel>;
    Search: RelocationOrderSearchModel;
    FilteredReportLoading: number;
    PackageLabelPdf: number;
    ProtocolPdf: number;
}

class DictionaryKeys {
  public static readonly OrderStatus = "RelocationOrderStatus";
}


@State<RelocationOrderStateModel>({
    name: 'RelocationOrderStateModel',
    defaults: {
      OrderStatus: null,
      OrderList: null,
      Search: null,
      Order: null,
      FilteredReportLoading: null,
      PackageLabelPdf: null,
      ProtocolPdf: null
    }
  })

  @Injectable()

  export class RelocationOrderState {

    constructor(
      private helperService: HelperService,
      private relocationOrderService: RelocationOrderService,
      private orderService: OrderService,
      private bannerService: BannerService,
      private storageService: StorageService
  ) { }

  @Selector()
  static FilteredReportLoading(state: RelocationOrderStateModel) {
    return state.FilteredReportLoading;
  }

  @Selector()
  static OrderStatus(state: RelocationOrderStateModel) {
    return state.OrderStatus;
  }

  @Selector()
  static OrderList(state: RelocationOrderStateModel) {
    return state.OrderList;
  }

  @Selector()
  static Order(state: RelocationOrderStateModel) {
    return state.Order;
  }

  @Selector()
  static OrderSearch(state: RelocationOrderStateModel) {
    return state.Search;
  }

  @Action(RelocationOrderActions.GetList)
  GetList(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.GetList
  ) {

    const errorMessage = 'Błąd pobierania listy zleceń relokacji.';

    context.setState(
      patch({
        OrderList: null
      })
    );

    this.relocationOrderService.getList(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            OrderList: ret
          })
        );

        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('OrderActions.GetList', error);

        this.bannerService.error(errorMessage);
      });
  }


  @Action(RelocationOrderActions.SetSearch)
  SetSearch(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions .SetSearch
  ) {

    action.Search.CurrentPage = 1;

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new RelocationOrderActions.GetList(action.Search));
  }

  @Action(RelocationOrderActions.SetListPage)
  SetListPage(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions .SetListPage
  ) {

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new RelocationOrderActions.GetList(action.Search));
  }

  @Action(RelocationOrderActions.ResetSearch)
  ResetSearch(
    context: StateContext<RelocationOrderStateModel>
  ) {

    const state = context.getState();

    const search = new RelocationOrderSearchModel();

    search.ItemsPerPage = state.Search.ItemsPerPage;

    context.setState(
      patch({
        Search: search
      }));

    context.dispatch(new RelocationOrderActions.GetList(search));
  }

  @Action(RelocationOrderActions.GetStatusDictionary)
  GetStatusDictionary(
    context: StateContext<RelocationOrderStateModel>
  ) {

    const errorMessage = 'Błąd pobierania statusów zleceń relokacji opon.';

    let storageKey = DictionaryKeys.OrderStatus;

    context.setState(
      patch({
        OrderStatus: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.OrderStatus) {
      this.relocationOrderService.getStatusDictionary().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              OrderStatus: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('TyreOrderActions.GetStatusDictionary', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(RelocationOrderActions.GetFilteredReport)
  GetFilteredReport(
      context: StateContext<RelocationOrderStateModel>,
      action: RelocationOrderActions.GetFilteredReport
   ) {
        function patchContext() {
          context.setState(
            patch ({
              FilteredReportLoading: Math.random()
            })
          );
        }

        const fileName = 'Raport-zlecenia-relokacji';
        this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`, 'Export danych');

        this.relocationOrderService.getFilteredReport(action.Search).subscribe (
              res => {
                const file = new File([res], `${fileName}-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                saveAs(file);
                patchContext();
              },
              res => {
                patchContext();
                this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
              },
              () => {
                patchContext();
                this.helperService.consoleLog(' @Action(SaleArticleActions.GetFilteredReport)');
              }
          );
  }

  @Action(RelocationOrderActions.DisposeSearch)
  DisposeSearch(
      context: StateContext<RelocationOrderStateModel>
    ) {

      context.setState(
        patch({
          Search: null
        }));
  }

  @Action(RelocationOrderActions.Set)
  Set(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.Set
  ) {
    const errorMessage = 'Błąd zapisu zmian zlecenia relokacji opon.';

    this.relocationOrderService.set(action.Order).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Zapisano zmiany do zlecenia relokacji opon ' + ret.Item.Number + '.');

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('RelocationOrderActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(RelocationOrderActions.Get)
  Get(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.Get
  ) {
    const errorMessage = 'Błąd odczytu szczegółów zlecenia relokacji opon.';

    this.relocationOrderService.get(action.OrderId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('RelocationOrderActions.Get', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(RelocationOrderActions.GetPackageLabelPdf)
  GetPackageLabelPdf(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.GetPackageLabelPdf
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie etykiety, proszę czekać.');

    this.relocationOrderService.getPackageLabelPdf(action.OrderId).subscribe(ret => {
      const file = new File([ret], `Etykieta-${this.helperService.TimeStamp()}.pdf`, { type: 'application/pdf' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('RelocationOrderActions.GetPackageLabelPdf', error);

        this.bannerService.error('Błąd pobierania etykiety.');
      },
      () => {
        context.setState(
          patch({
            PackageLabelPdf: Math.random()
          })
        );
      }
    );
  }

  @Action(RelocationOrderActions.GetOrderProtocolPdf)
  GetOrderProtocolPdf(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.GetOrderProtocolPdf
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie etykiety, proszę czekać.');

    this.relocationOrderService.getOrderProtocolPdf(action.OrderId).subscribe(ret => {
      const file = new File([ret], `Protokół-${this.helperService.TimeStamp()}.pdf`, { type: 'application/pdf' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('RelocationOrderActions.GetOrderProtocolPdf', error);

        this.bannerService.error('Błąd pobierania protokołu.');
      },
      () => {
        context.setState(
          patch({
            PackageLabelPdf: Math.random()
          })
        );
      }
    );
  }

  @Action(RelocationOrderActions.GetParcelProtocolPdf)
  GetParcelProtocolPdf(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.GetParcelProtocolPdf
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie etykiety, proszę czekać.');

    this.relocationOrderService.getParcelProtocolPdf(action.ParcelId).subscribe(ret => {
      const file = new File([ret], `Protokół-${this.helperService.TimeStamp()}.pdf`, { type: 'application/pdf' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('RelocationOrderActions.GetParcelProtocolPdf', error);

        this.bannerService.error('Błąd pobierania protokołu.');
      },
      () => {
        context.setState(
          patch({
            PackageLabelPdf: Math.random()
          })
        );
      }
    );
  }

  @Action(RelocationOrderActions.New)
  New(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.New
  ) {
    const errorMessage = 'Błąd utworzenia zlecenia relokacji opon.';

    this.relocationOrderService.new(action.Order).subscribe(
      ret => {

        action.Order.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Utworzono zlecenia relokacji opon ' + ret.Item.Number + '.');
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('RelocationOrderActions.New', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(RelocationOrderActions.GetTakoverTyreStateProtocolPdf)
  GetTakoverTyreStateProtocolPdf(
    context: StateContext<RelocationOrderStateModel>,
    action: RelocationOrderActions.GetTakoverTyreStateProtocolPdf
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie protokołu, proszę czekać.');

    this.relocationOrderService.getTakeoverTyreStateProtocolPdf(action.TakoverTyreStateId).subscribe(ret => {
      const file = new File([ret], `Protokół-${this.helperService.TimeStamp()}.pdf`, { type: 'application/pdf' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('RelocationOrderActions.GetTakoverTyreStateProtocolPdf', error);

        this.bannerService.error('Błąd pobierania protokołu.');
      },
      () => {
        context.setState(
          patch({
            PackageLabelPdf: Math.random()
          })
        );
      }
    );
  }
}

